import { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, ArrowRight, Check, X } from "lucide-react";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import MellanModal from "../components/MellanModal";
import BookingSummary from "../components/BookingSummary";
import BookingBar from "../components/BookingBar";
import { scrollToId } from "../components/Header";
import { SITE, SELECTION_KEY } from "../data/site";
import { trackEvent } from "../utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_TILES = [
  { id: "fonsterputs",   label: "Fönsterputs" },
  { id: "solpaneltvatt", label: "Solpaneltvätt" },
  { id: "altantvatt",    label: "Altantvätt" },
  { id: "marktvatt",     label: "Marktvätt" },
  { id: "annat",         label: "Annat" },
];

const FP_LEVEL_NAMES = ["Utsida", "In och utsida", "Premium"];
const PROPERTY_TYPES = ["Villa/Hus", "Lägenhet", "Företag/Kontor/Butik", "Annat"];

const initial = {
  name: "", email: "", phone: "",
  customer_type: "privat",
  address: "", message: "",
  services: [],
  fonsterputs_level: "Utsida",
  num_windows: "",
  property_type: "Villa/Hus",
  num_solar_panels: "",
  altan_info: "",
  mark_info: "",
  annat_info: "",
};

const readSelection = () => {
  try { return JSON.parse(sessionStorage.getItem(SELECTION_KEY)) || null; } catch { return null; }
};

export default function Quote() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(() => {
    // Deep link wins: /boka-offert?tjanst=solpaneltvatt preselects that service
    const t = searchParams.get("tjanst");
    const valid = t && SERVICE_TILES.some((x) => x.id === t);
    const s = readSelection();
    if (valid) return { ...initial, services: [t], fonsterputs_level: s?.level || initial.fonsterputs_level };
    if (s?.level) return { ...initial, services: ["fonsterputs"], fonsterputs_level: s.level };
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  // "Mellan fönstren" add-on state — only valid together with fönsterputs
  const [mellanFonstren, setMellanFonstren] = useState(() => !!readSelection()?.mellan);
  const [addonAsked, setAddonAsked] = useState(() => !!readSelection()?.asked);
  const [showMellan, setShowMellan] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const has = (id) => form.services.includes(id);

  // Keep the shared selection in sync so the home configurator reflects changes made here
  useEffect(() => {
    if (!has("fonsterputs")) return;
    try {
      sessionStorage.setItem(SELECTION_KEY, JSON.stringify({ level: form.fonsterputs_level, mellan: mellanFonstren, asked: true }));
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.fonsterputs_level, mellanFonstren, form.services]);

  const toggleService = (id) => {
    const adding = !form.services.includes(id);
    setForm((f) => ({ ...f, services: adding ? [...f.services, id] : f.services.filter((x) => x !== id) }));
    if (id === "fonsterputs") {
      // Selecting the main window-cleaning service opens the contextual add-on offer (once per session)
      if (adding && !addonAsked) setShowMellan(true);
      // Add-on becomes invalid without the main service
      if (!adding) setMellanFonstren(false);
    }
  };

  const addMellan = () => {
    setMellanFonstren(true);
    setAddonAsked(true);
    setShowMellan(false);
    toast.success("Mellan fönstren tillagt i din bokning.");
  };
  const declineMellan = () => {
    setAddonAsked(true);
    setShowMellan(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error("Välj minst en tjänst du vill ha hjälp med.");
      return;
    }
    setLoading(true);
    try {
      // Only include conditional fields when relevant
      const payload = {
        name: form.name, email: form.email, phone: form.phone,
        customer_type: form.customer_type, address: form.address, message: form.message,
        services: form.services,
        fonsterputs_level: has("fonsterputs") ? form.fonsterputs_level : null,
        num_windows: has("fonsterputs") ? form.num_windows : null,
        property_type: has("fonsterputs") ? form.property_type : null,
        mellan_fonstren: has("fonsterputs") ? mellanFonstren : false,
        num_solar_panels: has("solpaneltvatt") ? form.num_solar_panels : null,
        altan_info: has("altantvatt") ? form.altan_info : null,
        mark_info: has("marktvatt") ? form.mark_info : null,
        annat_info: has("annat") ? form.annat_info : null,
      };
      await axios.post(`${API}/quotes`, payload);
      trackEvent("generate_lead", { services: form.services.join(","), level: has("fonsterputs") ? form.fonsterputs_level : "", mellan_fonstren: mellanFonstren });
      try { sessionStorage.removeItem(SELECTION_KEY); } catch { /* ignore */ }
      setDone(true);
      toast.success("Tack! Din offertförfrågan har skickats.");
      setForm(initial);
      setMellanFonstren(false);
      setAddonAsked(false);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Något gick fel. Försök igen eller ring oss direkt.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-brand-iceBg border border-slate-200 p-4 focus:ring-2 focus:ring-brand-blue focus:border-transparent focus:outline-none transition-all";
  const label = "block text-sm font-semibold text-brand-ink mb-1.5";

  return (
    <div data-testid="quote-page" className="bg-white">
      <Seo title="Boka kostnadsfri offert – Se Klart Ut" description="Begär en kostnadsfri offert på fönsterputs, solpaneltvätt, altantvätt eller marktvätt i hela Skaraborg. Snabbt svar, utan förpliktelser." path="/boka-offert" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand-blue">Boka</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.08] text-brand-ink">
              Kostnadsfri offert
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">Välj en eller flera tjänster – vi återkommer snabbt med ett tydligt prisförslag, utan förpliktelser.</p>
          </Reveal>
          <div className="mt-8 space-y-4">
            {["Svar inom kort", "Tydligt pris utan dolda avgifter", "Försäkrat & Se Klart Ut-garanti"].map((t, i) => (
              <Reveal key={t} delay={0.1 + i * 0.07}>
                <div className="flex items-center gap-3 text-brand-ink text-sm font-medium">
                  <CheckCircle2 className="h-5 w-5 text-brand-blue shrink-0" strokeWidth={1.75} /> {t}
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8 rounded-2xl bg-brand-iceBg p-6 flex items-start gap-3">
              <ShieldCheck className="h-6 w-6 text-brand-blue shrink-0" strokeWidth={1.75} />
              <p className="text-sm text-slate-600">Föredrar du att prata direkt? Ring <a href={SITE.phoneHref} className="text-brand-blue font-semibold">{SITE.phone}</a> eller mejla <a href={SITE.emailHref} className="text-brand-blue font-semibold">{SITE.email}</a>.</p>
            </div>
          </Reveal>
          {/* STICKY BOOKING SUMMARY (desktop) */}
          <Reveal delay={0.25}>
            <div className="mt-6 hidden lg:block sticky top-28 rounded-2xl bg-white border border-slate-100 shadow-[0_8px_30px_rgb(7,22,44,0.06)] p-6">
              <BookingSummary
                services={form.services}
                fonsterputsLevel={has("fonsterputs") ? form.fonsterputs_level : null}
                mellanFonstren={mellanFonstren}
                onRemoveMellan={() => setMellanFonstren(false)}
              />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="quote-success" className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_8px_30px_rgb(7,22,44,0.06)] text-center">
                <div className="h-16 w-16 rounded-full bg-success/10 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-success" strokeWidth={1.75} />
                </div>
                <h2 className="mt-6 font-heading text-2xl font-bold text-brand-ink">Tack för din förfrågan!</h2>
                <p className="mt-3 text-slate-600">Vi har tagit emot din offertförfrågan och återkommer så snart som möjligt.</p>
                <button onClick={() => setDone(false)} data-testid="quote-new-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-3.5 hover:bg-brand-blueHover transition-colors">
                  Skicka en ny förfrågan <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="bg-white rounded-3xl p-6 sm:p-8 md:p-10 border border-slate-100 shadow-[0_8px_30px_rgb(7,22,44,0.06)] space-y-8">
                {/* CONTACT */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={label} htmlFor="q-name">Namn *</label>
                    <input id="q-name" required value={form.name} onChange={set("name")} data-testid="quote-name-input" className={inputCls} placeholder="För- och efternamn" />
                  </div>
                  <div>
                    <label className={label} htmlFor="q-phone">Telefon *</label>
                    <input id="q-phone" required value={form.phone} onChange={set("phone")} data-testid="quote-phone-input" className={inputCls} placeholder="070-123 45 67" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label} htmlFor="q-email">E-post *</label>
                    <input id="q-email" required type="email" value={form.email} onChange={set("email")} data-testid="quote-email-input" className={inputCls} placeholder="din@epost.se" />
                  </div>
                  <div>
                    <label className={label} htmlFor="q-type">Jag är</label>
                    <select id="q-type" value={form.customer_type} onChange={set("customer_type")} data-testid="quote-customertype-select" className={inputCls}>
                      <option value="privat">Privatperson</option>
                      <option value="foretag">Företag</option>
                    </select>
                  </div>
                  <div>
                    <label className={label} htmlFor="q-address">Adress / Område</label>
                    <input id="q-address" value={form.address} onChange={set("address")} data-testid="quote-address-input" className={inputCls} placeholder="Gata, ort (t.ex. Skövde)" />
                  </div>
                </div>

                {/* SERVICES MULTI-SELECT */}
                <div>
                  <label className="block text-base font-bold text-brand-ink mb-3">Vad vill du ha hjälp med? *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="services-picker">
                    {SERVICE_TILES.map((s) => {
                      const on = has(s.id);
                      return (
                        <button
                          type="button"
                          key={s.id}
                          onClick={() => toggleService(s.id)}
                          data-testid={`service-tile-${s.id}`}
                          aria-pressed={on}
                          className={`w-full flex items-center justify-between gap-3 rounded-2xl px-5 py-4 md:py-5 text-left transition-all duration-200 min-h-[56px] ${on ? "bg-brand-blue text-white border-2 border-brand-blue shadow-[0_6px_20px_rgba(14,98,254,0.25)]" : "bg-brand-iceBg text-brand-ink border-2 border-transparent hover:border-brand-blue/50"}`}
                        >
                          <span className="font-semibold text-base">{s.label}</span>
                          <span className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 transition-transform duration-200 ${on ? "bg-white scale-105" : "bg-white border border-slate-300"}`}>
                            {on && <Check className="h-4 w-4 text-brand-blue" strokeWidth={2.5} />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* CONDITIONAL: FÖNSTERPUTS */}
                {has("fonsterputs") && (
                  <div className="rounded-2xl border border-brand-blue/25 bg-brand-iceBg p-6 space-y-5" data-testid="fp-details">
                    <div>
                      <label className={label}>Vilken fönsterputs vill du ha?</label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-label="Nivå av fönsterputs">
                        {FP_LEVEL_NAMES.map((lvl) => {
                          const on = form.fonsterputs_level === lvl;
                          return (
                            <button
                              type="button"
                              key={lvl}
                              role="radio"
                              aria-checked={on}
                              onClick={() => setForm({ ...form, fonsterputs_level: lvl })}
                              data-testid={`fp-level-${lvl}`}
                              className={`rounded-xl px-4 py-3 min-h-[52px] text-sm font-semibold transition-all border-2 ${on ? "bg-brand-blue text-white border-brand-blue" : "bg-white text-brand-ink border-slate-200 hover:border-brand-blue/60"}`}
                            >
                              {lvl}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    {/* ADD-ON STATE */}
                    {mellanFonstren ? (
                      <div data-testid="mellan-chip" className="flex items-center justify-between gap-3 rounded-xl bg-white border border-success/40 px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-semibold text-brand-ink">
                          <Check className="h-4 w-4 text-success" strokeWidth={2.5} /> Tillval: Mellan fönstren
                        </span>
                        <button
                          type="button"
                          onClick={() => setMellanFonstren(false)}
                          aria-label="Ta bort tillvalet Mellan fönstren"
                          data-testid="mellan-remove"
                          className="h-7 w-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                        >
                          <X className="h-3.5 w-3.5 text-slate-600" />
                        </button>
                      </div>
                    ) : addonAsked ? (
                      <button
                        type="button"
                        onClick={() => setShowMellan(true)}
                        data-testid="mellan-reopen"
                        className="text-sm font-semibold text-brand-blue hover:text-brand-blueHover transition-colors"
                      >
                        + Lägg till Mellan fönstren (kopplade fönster)
                      </button>
                    ) : null}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className={label} htmlFor="q-windows">Ungefär hur många fönster?</label>
                        <input id="q-windows" value={form.num_windows} onChange={set("num_windows")} data-testid="quote-num-windows" className={inputCls} placeholder="t.ex. 15" inputMode="numeric" />
                      </div>
                      <div>
                        <label className={label} htmlFor="q-property">Typ av fastighet</label>
                        <select id="q-property" value={form.property_type} onChange={set("property_type")} data-testid="quote-property-type" className={inputCls}>
                          {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* CONDITIONAL: SOLPANELTVÄTT */}
                {has("solpaneltvatt") && (
                  <div className="rounded-2xl border border-brand-blue/25 bg-brand-iceBg p-6">
                    <label className={label} htmlFor="q-solar">Ungefär hur många solpaneler?</label>
                    <input id="q-solar" value={form.num_solar_panels} onChange={set("num_solar_panels")} data-testid="quote-num-solar" className={inputCls} placeholder="t.ex. 12" inputMode="numeric" />
                  </div>
                )}

                {/* CONDITIONAL: ALTAN */}
                {has("altantvatt") && (
                  <div className="rounded-2xl border border-brand-blue/25 bg-brand-iceBg p-6">
                    <label className={label} htmlFor="q-altan">Berätta gärna ungefär hur stor altanen är</label>
                    <input id="q-altan" value={form.altan_info} onChange={set("altan_info")} data-testid="quote-altan" className={inputCls} placeholder="t.ex. ca 20 kvm" />
                  </div>
                )}

                {/* CONDITIONAL: MARK */}
                {has("marktvatt") && (
                  <div className="rounded-2xl border border-brand-blue/25 bg-brand-iceBg p-6">
                    <label className={label} htmlFor="q-mark">Berätta gärna vad som ska tvättas och ungefär hur stor ytan är</label>
                    <input id="q-mark" value={form.mark_info} onChange={set("mark_info")} data-testid="quote-mark" className={inputCls} placeholder="t.ex. uppfart, ca 60 kvm" />
                  </div>
                )}

                {/* CONDITIONAL: ANNAT */}
                {has("annat") && (
                  <div className="rounded-2xl border border-brand-blue/25 bg-brand-iceBg p-6">
                    <label className={label} htmlFor="q-annat">Berätta gärna vad du vill ha hjälp med</label>
                    <input id="q-annat" value={form.annat_info} onChange={set("annat_info")} data-testid="quote-annat" className={inputCls} placeholder="Beskriv kort" />
                  </div>
                )}

                {/* MESSAGE */}
                <div>
                  <label className={label} htmlFor="q-message">Meddelande</label>
                  <textarea id="q-message" rows={4} value={form.message} onChange={set("message")} data-testid="quote-message-input" className={`${inputCls} resize-none`} placeholder="Berätta gärna mer om jobbet, exempelvis antal fönster, storlek på altan, antal solpaneler, yta som ska tvättas eller andra önskemål." />
                </div>

                <div>
                  <button type="submit" disabled={loading} data-testid="quote-submit-button" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue text-white font-semibold px-8 py-4 min-h-[56px] hover:bg-brand-blueHover hover:shadow-[0_14px_35px_rgba(14,98,254,0.3)] disabled:opacity-60 active:scale-[0.99] transition-all">
                    {loading ? "Skickar…" : <>Skicka förfrågan <ArrowRight className="h-4 w-4" /></>}
                  </button>
                  <p className="mt-4 text-center text-xs text-slate-400">Genom att skicka godkänner du att vi kontaktar dig angående din förfrågan.</p>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* ADD-ON MODAL / MOBILE BOTTOM SHEET */}
      <MellanModal open={showMellan} onAdd={addMellan} onDecline={declineMellan} />

      {/* MOBILE STICKY BOOKING BAR */}
      <BookingBar
        services={form.services}
        fonsterputsLevel={has("fonsterputs") ? form.fonsterputs_level : null}
        mellanFonstren={mellanFonstren}
        onRemoveMellan={() => setMellanFonstren(false)}
        onContinue={() => scrollToId("[data-testid='quote-form']")}
        continueLabel="Slutför bokning"
      />
    </div>
  );
}
